import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBookOpen,
  faCirclePlay,
  faCodeBranch,
  faKey,
  faMagnifyingGlass,
  faMessage,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import ApiConnectionLibrary from "../Library/ApiConnectionLibrary";

const apiConnectionVideos = [
  "Create an API Connection",
  "Sending API Messages (using Postman)",
];

export default function ApiConnectionsInformation() {
  const [showApiConnections, setShowApiConnections] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState("");
  const videoSearchInputRef = useRef(null);
  const normalisedVideoSearchTerm = videoSearchTerm.trim().toLowerCase();
  const filteredApiConnectionVideos = apiConnectionVideos.filter((video) =>
    video.toLowerCase().includes(normalisedVideoSearchTerm)
  );

  return (
    <main className="api-connections-information container-fluid py-4 py-lg-5">
      <div className="row g-4">
        <section className="col-lg-8" aria-labelledby="api-connections-overview-heading">
          <div className="api-connections-information__card card border-0 rounded-4 p-4 p-lg-5 h-100">
            <h2 id="api-connections-overview-heading" className="h3 fw-bold mb-3">
              About API Connections
            </h2>
            <p className="text-secondary">
              Connect Boomerang with your own systems to securely exchange data and support
              automated communication workflows.
            </p>
            <div className="row g-3 mt-1">
              <Feature icon={faCodeBranch} title="Connected workflows">
                Use external data and services as part of your Boomerang processes.
              </Feature>
              <Feature icon={faMessage} title="Messaging integration">
                Send and manage messages from your existing applications and services.
              </Feature>
              <Feature icon={faKey} title="Flexible authentication">
                Configure the connection details and credentials required by each service.
              </Feature>
              <Feature icon={faShieldHalved} title="Secure configuration">
                Keep connection settings in one place for consistent, reusable integrations.
              </Feature>
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="btn bg-colored-reverse rounded-pill text-white px-4"
                onClick={() => setShowApiConnections(true)}
              >
                Connect New API <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        <aside className="col-lg-4" aria-labelledby="api-connections-help-heading">
          <div className="api-connections-information__card card border-0 rounded-4 p-4 p-lg-5 h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FontAwesomeIcon className="text-purple fs-3" icon={faBookOpen} aria-hidden="true" />
              <h2 id="api-connections-help-heading" className="h3 fw-bold mb-0">API Connections help</h2>
            </div>
            <p className="text-secondary">
              Visit the help centre for step-by-step guidance and answers to common API
              Connections questions.
            </p>
            <a
              href="https://help.uatboomcomms.com/"
              target="_blank"
              rel="noreferrer"
              className="api-connections-information__link fw-bold text-decoration-none"
            >
              Open API Connections help <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
            <div className="api-connections-information__videos mt-4 pt-4">
              <div className="d-flex align-items-start gap-2 mb-3">
                <FontAwesomeIcon className="text-purple fs-4 mt-1" icon={faCirclePlay} aria-hidden="true" />
                <div>
                  <h2 id="api-connections-videos-heading" className="h3 fw-bold mb-1">
                    API Connections videos
                  </h2>
                  <p className="small text-secondary mb-0">
                    Learn how to create connections and send API messages.
                  </p>
                </div>
              </div>
              <form
                className="api-connections-information__video-search input-group input-group-sm mb-3"
                onSubmit={(event) => event.preventDefault()}
              >
                <label className="visually-hidden" htmlFor="api-connections-video-search">
                  Search API Connections videos
                </label>
                <input
                  ref={videoSearchInputRef}
                  id="api-connections-video-search"
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search API Connections videos"
                  value={videoSearchTerm}
                  onChange={(event) => setVideoSearchTerm(event.target.value)}
                />
                <button
                  type="submit"
                  className="api-connections-information__video-search-button btn"
                  aria-label="Search API Connections videos"
                  onClick={() => videoSearchInputRef.current?.focus()}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                </button>
              </form>
              <ul className="api-connections-information__video-list list-unstyled mb-0">
                {filteredApiConnectionVideos.map((video) => (
                  <li key={video}>
                    <a
                      href="https://help.uatboomcomms.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="api-connections-information__video-link d-flex align-items-center justify-content-between rounded-3 p-3 text-decoration-none"
                    >
                      <span>{video}</span>
                      <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
              {normalisedVideoSearchTerm && filteredApiConnectionVideos.length === 0 && (
                <p className="api-connections-information__video-search-empty mb-0">
                  No API Connections videos found.
                </p>
              )}
            </div>
          </div>
        </aside>

      </div>
      {showApiConnections && (
        <section
          className="api-connections-information__library mt-4"
          aria-label="API Connections Library"
        >
          <ApiConnectionLibrary />
        </section>
      )}
    </main>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="col-md-6">
      <div className="api-connections-information__feature h-100 rounded-3 p-3">
        <FontAwesomeIcon className="text-purple me-2" icon={icon} aria-hidden="true" />
        <strong>{title}</strong>
        <div className="small text-secondary mt-2">{children}</div>
      </div>
    </div>
  );
}